import React, { useState } from 'react';
import { ChevronRight, Droplets, AlertCircle, CheckCircle, Sparkles, ArrowDown } from 'lucide-react';

function App() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const questions = [
    {
      id: 'primary_concern',
      question: "What's your biggest hair or skin struggle?",
      subtitle: "Select the one that bothers you most",
      options: [
        { value: 'hair_loss', label: 'Hair shedding or thinning', icon: '💇‍♀️' },
        { value: 'acne', label: 'Breakouts or acne', icon: '😔' },
        { value: 'dry_skin', label: 'Dry, itchy, or irritated skin', icon: '🌵' },
        { value: 'frizzy_hair', label: 'Frizzy, tangled, or damaged hair', icon: '🌪️' },
        { value: 'eczema', label: 'Eczema or psoriasis flare-ups', icon: '🔴' },
        { value: 'dull_hair', label: 'Dull hair or fading color', icon: '✨' }
      ]
    },
    {
      id: 'spending',
      question: "How much do you spend monthly on hair & skincare products?",
      subtitle: "Let's see if you're treating the symptom instead of the cause",
      options: [
        { value: 'under_50', label: 'Under $50', icon: '💵' },
        { value: '50_100', label: '$50 - $100', icon: '💰' },
        { value: '100_200', label: '$100 - $200', icon: '💸' },
        { value: 'over_200', label: 'Over $200', icon: '🤑' }
      ]
    },
    {
      id: 'tried_products',
      question: "Have you noticed your skin or hair issues get worse after showering?",
      subtitle: "This is a major sign it's your water quality",
      options: [
        { value: 'yes_always', label: 'Yes, all the time', icon: '😫' },
        { value: 'yes_sometimes', label: 'Yes, sometimes', icon: '🤔' },
        { value: 'not_sure', label: "I'm not sure", icon: '🤷‍♀️' },
        { value: 'no', label: 'No, not really', icon: '😌' }
      ]
    },
    {
      id: 'water_feel',
      question: "Does your skin feel tight, dry, or like there's a film on it after showering?",
      subtitle: "That's chlorine stripping your natural oils",
      options: [
        { value: 'yes_film', label: 'Yes, feels like a film or residue', icon: '😣' },
        { value: 'yes_tight', label: 'Yes, tight and dry', icon: '😖' },
        { value: 'sometimes', label: 'Only sometimes', icon: '😐' },
        { value: 'no', label: 'No, feels normal', icon: '😊' }
      ]
    },
    {
      id: 'location_change',
      question: "Have you ever noticed your hair/skin improve when traveling or moving?",
      subtitle: "Different locations = different water quality",
      options: [
        { value: 'yes_better', label: 'Yes! My hair/skin was so much better', icon: '🌟' },
        { value: 'yes_worse', label: 'Yes, but it got worse', icon: '😞' },
        { value: 'not_sure', label: 'Never really noticed', icon: '🤷' },
        { value: 'no_travel', label: "Haven't traveled much", icon: '🏠' }
      ]
    },
    {
      id: 'hair_type',
      question: "What's your hair type?",
      subtitle: "We'll personalize your recommendation",
      options: [
        { value: 'straight', label: 'Straight', icon: '📏' },
        { value: 'wavy', label: 'Wavy', icon: '〰️' },
        { value: 'curly', label: 'Curly', icon: '🌀' },
        { value: 'coily', label: 'Coily/Kinky', icon: '🔄' },
        { value: 'color_treated', label: 'Color-treated', icon: '🎨' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      setTimeout(() => setStep(step + 1), 300);
    }
  };

  const Results = () => {
    const concern = answers.primary_concern;
    const spending = answers.spending;
    
    let painMessage = "";
    let solution = "";
    
    if (concern === 'hair_loss') {
      painMessage = "Chlorine in your shower water is stripping your hair's natural oils, weakening roots and causing excessive shedding.";
      solution = "Our filtered showerhead removes 99% of chlorine, helping reduce hair loss by up to 47% in the first 3 months.";
    } else if (concern === 'acne') {
      painMessage = "Your shower water contains chlorine and heavy metals that strip your skin's protective barrier, leading to breakouts.";
      solution = "Clean water = clean skin. Most users see clearer skin within 2 weeks of filtering out harsh chemicals.";
    } else if (concern === 'dry_skin' || concern === 'eczema') {
      painMessage = "Chlorine acts like bleach on your skin, removing moisture and triggering inflammation and irritation.";
      solution = "Filtered water preserves your skin's natural moisture barrier, reducing dryness and flare-ups dramatically.";
    } else if (concern === 'frizzy_hair') {
      painMessage = "Hard water minerals and chlorine create buildup that makes hair rough, tangled, and impossible to manage.";
      solution = "Our filter removes these minerals, giving you softer, smoother, more manageable hair from day one.";
    } else {
      painMessage = "Your shower water is working against you, damaging what you're trying to protect.";
      solution = "Clean water is the foundation of healthy hair and skin. It's step zero.";
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Here's What's Happening...</h2>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6 rounded-r-lg">
            <h3 className="font-bold text-lg text-red-900 mb-2">The Problem:</h3>
            <p className="text-red-800">{painMessage}</p>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-6 mb-8 rounded-r-lg">
            <h3 className="font-bold text-lg text-green-900 mb-2">The Solution:</h3>
            <p className="text-green-800">{solution}</p>
          </div>

          {(spending === '100_200' || spending === 'over_200') && (
            <div className="bg-yellow-50 border border-yellow-200 p-6 mb-8 rounded-lg">
              <p className="text-yellow-900 font-semibold mb-2">💡 You're spending {spending === 'over_200' ? '200+' : '$100-200'}/month on products...</p>
              <p className="text-yellow-800">But if your water is the problem, you're just treating symptoms. The Lucinn Pro Filtered showerhead could save you thousands while actually fixing the root cause.</p>
            </div>
          )}

          <div className="space-y-4 mb-8">
            <h3 className="font-bold text-xl text-gray-900">What You'll Experience:</h3>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700">Noticeable improvement in hair softness and skin hydration within 1-2 weeks</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700">Reduced breakouts, redness, and irritation</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700">Less hair shedding and stronger, healthier roots</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700">Hair color that lasts longer and stays vibrant</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-green-600 p-8 rounded-xl text-white mb-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4">
              <Sparkles className="w-8 h-8 text-white animate-spin-slow" />
            </div>
            <h3 className="text-3xl font-bold mb-4">Special Offer: 60% OFF Today Only!</h3>
            <p className="text-xl mb-6">Get your personalized filtered showerhead and start seeing results in just 1-2 weeks</p>
            
            {/* Animated CTA Section with Arrows */}
            <div className="relative">
      
              {/* Pulsing glow effect */}
              <div className="absolute inset-0 bg-white rounded-xl blur-xl opacity-30 animate-pulse"></div>
              
              {/* Main CTA Button */}
              <a 
                href="https://trk.newsbeyondborders.com/cf/click/1" 
                className="relative inline-flex items-center justify-center w-full bg-white text-blue-600 font-bold text-xl px-8 py-5 rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-lg transform hover:scale-105 animate-pulse-slow"
              >
                <Sparkles className="w-6 h-6 mr-3 animate-spin-slow" />
                Claim My 60% OFF Now →
                <Sparkles className="w-6 h-6 ml-3 animate-spin-slow" />
              </a>
            </div>
            
            <p className="text-sm text-blue-100 mt-4">✓ 60-Day Money-Back Guarantee  ✓ Free Shipping  ✓ 40,000+ Happy Customers</p>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center py-6 border-t border-gray-200">
            <div>
              <p className="text-2xl font-bold text-gray-900">40,000+</p>
              <p className="text-sm text-gray-600">Happy Customers</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">4.8★</p>
              <p className="text-sm text-gray-600">Average Rating</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">60 Days</p>
              <p className="text-sm text-gray-600">Risk-Free Trial</p>
            </div>
          </div>

          <div className="text-center text-sm text-gray-600 mt-6">
            <p>🔒 Secure checkout • Same-day shipping • 100% satisfaction guaranteed</p>
          </div>
        </div>

        {/* Custom CSS for animations */}
        <style jsx>{`
          @keyframes pulse-slow {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.95;
              transform: scale(1.02);
            }
          }

          @keyframes spin-slow {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          .animate-pulse-slow {
            animation: pulse-slow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          .animate-spin-slow {
            animation: spin-slow 3s linear infinite;
          }

          .animation-delay-150 {
            animation-delay: 150ms;
          }
        `}</style>
      </div>
    );
  };

  if (step === questions.length) {
    return <Results />;
  }

  const currentQuestion = questions[step];
  const progress = ((step + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 md:p-12">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Question {step + 1} of {questions.length}</span>
            <span className="text-sm font-medium text-blue-600">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-600 to-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{currentQuestion.question}</h2>
          <p className="text-gray-600">{currentQuestion.subtitle}</p>
        </div>

        <div className="space-y-3">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleAnswer(currentQuestion.id, option.value)}
              className="w-full p-5 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 text-left group"
            >
              <div className="flex items-center space-x-4">
                <span className="text-3xl">{option.icon}</span>
                <span className="text-lg font-medium text-gray-800 group-hover:text-blue-600">{option.label}</span>
                <ChevronRight className="ml-auto w-6 h-6 text-gray-400 group-hover:text-blue-600" />
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <Droplets className="w-4 h-4" />
            <span>Join 40,000+ people with healthier hair & skin</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;